import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  InsightService,
  GlobalDashboard,
  SiteDashboard,
  TrendDataPoint,
  AgentAlert,
  SiteAgentStatusResponse,
  OrgAgentStatusResponse,
  AlertPagination,
  AlertStatus,
  AgentType,
  AxiosErrorResponse,
  GetAgentAlertsParams,
} from "@/lib/api/services/insightService";

export interface InsightState {
  globalDashboard: GlobalDashboard | null;
  siteDashboard: SiteDashboard | null;

  totalAssetsTrend: TrendDataPoint[] | null;
  goodHealthTrend: TrendDataPoint[] | null;
  needingRepairTrend: TrendDataPoint[] | null;
  lostMissingTrend: TrendDataPoint[] | null;
  complianceTrend: TrendDataPoint[] | null;
  assetUtilizationTrend: TrendDataPoint[] | null;

  globalTotalAssetsTrend: TrendDataPoint[] | null;
  globalGoodHealthTrend: TrendDataPoint[] | null;
  globalNeedingRepairTrend: TrendDataPoint[] | null;
  globalLostMissingTrend: TrendDataPoint[] | null;
  globalComplianceTrend: TrendDataPoint[] | null;
  globalAssetUtilizationTrend: TrendDataPoint[] | null;

  alerts: AgentAlert[];
  alertPagination: AlertPagination | null;

  siteAgentStatus: SiteAgentStatusResponse | null;
  orgAgentStatus: OrgAgentStatusResponse | null;

  lastRunResult: {
    agentType: string;
    alertsGenerated: number;
    siteId?: number;
    organizationId?: number;
  } | null;

  isLoading: boolean;
  isLoadingAlerts: boolean;
  isLoadingStatus: boolean;
  isRunningAgent: boolean;
  isDismissing: boolean;
  isAcknowledging: boolean;

  error: string | null;
  selectedOrganizationId: number | null;
  selectedSiteId: number | null;
  lastUpdated: string | null;
}

const defaultInitialState: InsightState = {
  globalDashboard: null,
  siteDashboard: null,
  totalAssetsTrend: null,
  goodHealthTrend: null,
  needingRepairTrend: null,
  lostMissingTrend: null,
  complianceTrend: null,
  assetUtilizationTrend: null,
  globalTotalAssetsTrend: null,
  globalGoodHealthTrend: null,
  globalNeedingRepairTrend: null,
  globalLostMissingTrend: null,
  globalComplianceTrend: null,
  globalAssetUtilizationTrend: null,
  alerts: [],
  alertPagination: null,
  siteAgentStatus: null,
  orgAgentStatus: null,
  lastRunResult: null,
  isLoading: false,
  isLoadingAlerts: false,
  isLoadingStatus: false,
  isRunningAgent: false,
  isDismissing: false,
  isAcknowledging: false,
  error: null,
  selectedOrganizationId: null,
  selectedSiteId: null,
  lastUpdated: null,
};

const initialState: InsightState = defaultInitialState;

const handleApiError = (error: unknown): string => {
  const axiosError = error as AxiosErrorResponse;
  if (axiosError.response?.data) {
    const data = axiosError.response.data;
    if (Array.isArray(data.message)) return data.message[0];
    return data.message || "An unexpected error occurred";
  }
  return (axiosError.message as string) || "An unexpected error occurred";
};

export interface SiteTrendParams {
  siteId: number;
  startDate?: string;
  endDate?: string;
}

export interface GlobalTrendParams {
  organizationId: number;
  startDate?: string;
  endDate?: string;
}

export type { GetAgentAlertsParams };

export const getGlobalDashboard = createAsyncThunk(
  "insight/getGlobalDashboard",
  async (
    { organizationId }: { organizationId: number },
    { rejectWithValue },
  ) => {
    try {
      return await InsightService.getGlobalDashboard(organizationId);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getSiteDashboard = createAsyncThunk(
  "insight/getSiteDashboard",
  async ({ siteId }: { siteId: number }, { rejectWithValue }) => {
    try {
      return await InsightService.getSiteDashboard(siteId);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getTotalAssetsTrend = createAsyncThunk(
  "insight/getTotalAssetsTrend",
  async (
    { siteId, startDate, endDate }: SiteTrendParams,
    { rejectWithValue },
  ) => {
    try {
      return await InsightService.getTotalAssetsTrend(
        siteId,
        startDate,
        endDate,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getGoodHealthTrend = createAsyncThunk(
  "insight/getGoodHealthTrend",
  async (
    { siteId, startDate, endDate }: SiteTrendParams,
    { rejectWithValue },
  ) => {
    try {
      return await InsightService.getGoodHealthTrend(
        siteId,
        startDate,
        endDate,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getNeedingRepairTrend = createAsyncThunk(
  "insight/getNeedingRepairTrend",
  async (
    { siteId, startDate, endDate }: SiteTrendParams,
    { rejectWithValue },
  ) => {
    try {
      return await InsightService.getNeedingRepairTrend(
        siteId,
        startDate,
        endDate,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getLostMissingTrend = createAsyncThunk(
  "insight/getLostMissingTrend",
  async (
    { siteId, startDate, endDate }: SiteTrendParams,
    { rejectWithValue },
  ) => {
    try {
      return await InsightService.getLostMissingTrend(
        siteId,
        startDate,
        endDate,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getComplianceTrend = createAsyncThunk(
  "insight/getComplianceTrend",
  async (
    { siteId, startDate, endDate }: SiteTrendParams,
    { rejectWithValue },
  ) => {
    try {
      return await InsightService.getComplianceTrend(
        siteId,
        startDate,
        endDate,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getAssetUtilizationTrend = createAsyncThunk(
  "insight/getAssetUtilizationTrend",
  async (
    { siteId, startDate, endDate }: SiteTrendParams,
    { rejectWithValue },
  ) => {
    try {
      return await InsightService.getAssetUtilizationTrend(
        siteId,
        startDate,
        endDate,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getGlobalTotalAssetsTrend = createAsyncThunk(
  "insight/getGlobalTotalAssetsTrend",
  async (
    { organizationId, startDate, endDate }: GlobalTrendParams,
    { rejectWithValue },
  ) => {
    try {
      return await InsightService.getGlobalTotalAssetsTrend(
        organizationId,
        startDate,
        endDate,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getGlobalGoodHealthTrend = createAsyncThunk(
  "insight/getGlobalGoodHealthTrend",
  async (
    { organizationId, startDate, endDate }: GlobalTrendParams,
    { rejectWithValue },
  ) => {
    try {
      return await InsightService.getGlobalGoodHealthTrend(
        organizationId,
        startDate,
        endDate,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getGlobalNeedingRepairTrend = createAsyncThunk(
  "insight/getGlobalNeedingRepairTrend",
  async (
    { organizationId, startDate, endDate }: GlobalTrendParams,
    { rejectWithValue },
  ) => {
    try {
      return await InsightService.getGlobalNeedingRepairTrend(
        organizationId,
        startDate,
        endDate,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getGlobalLostMissingTrend = createAsyncThunk(
  "insight/getGlobalLostMissingTrend",
  async (
    { organizationId, startDate, endDate }: GlobalTrendParams,
    { rejectWithValue },
  ) => {
    try {
      return await InsightService.getGlobalLostMissingTrend(
        organizationId,
        startDate,
        endDate,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getGlobalComplianceTrend = createAsyncThunk(
  "insight/getGlobalComplianceTrend",
  async (
    { organizationId, startDate, endDate }: GlobalTrendParams,
    { rejectWithValue },
  ) => {
    try {
      return await InsightService.getGlobalComplianceTrend(
        organizationId,
        startDate,
        endDate,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getGlobalAssetUtilizationTrend = createAsyncThunk(
  "insight/getGlobalAssetUtilizationTrend",
  async (
    { organizationId, startDate, endDate }: GlobalTrendParams,
    { rejectWithValue },
  ) => {
    try {
      return await InsightService.getGlobalAssetUtilizationTrend(
        organizationId,
        startDate,
        endDate,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getAgentAlerts = createAsyncThunk(
  "insight/getAgentAlerts",
  async (params: GetAgentAlertsParams, { rejectWithValue }) => {
    try {
      return await InsightService.getAgentAlerts(params);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getAgentStatusForSite = createAsyncThunk(
  "insight/getAgentStatusForSite",
  async ({ siteId }: { siteId: number }, { rejectWithValue }) => {
    try {
      return await InsightService.getAgentStatusForSite(siteId);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getAgentStatusForOrganization = createAsyncThunk(
  "insight/getAgentStatusForOrganization",
  async (
    { organizationId }: { organizationId: number },
    { rejectWithValue },
  ) => {
    try {
      return await InsightService.getAgentStatusForOrganization(organizationId);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const updateAlertTask = createAsyncThunk(
  "insight/updateAlertTask",
  async (
    {
      alertId,
      taskId,
      status,
    }: { alertId: number; taskId: string; status: "approved" | "ignored" },
    { rejectWithValue },
  ) => {
    try {
      const response = await InsightService.updateAlertTask(
        alertId,
        taskId,
        status,
      );
      return response.alert;
    } catch (err) {
      const error = err as AxiosErrorResponse;
      return rejectWithValue(
        error.response?.data?.message || "Failed to update task",
      );
    }
  },
);

export const activateAgent = createAsyncThunk(
  "insight/activateAgent",
  async (
    params: { agentType: AgentType; organizationId?: number; siteId?: number },
    { rejectWithValue },
  ) => {
    try {
      if (params.organizationId) {
        return await InsightService.activateOrgAgent(
          params.agentType,
          params.organizationId,
        );
      } else if (params.siteId) {
        return await InsightService.activateSiteAgent(
          params.agentType,
          params.siteId,
        );
      }
      return rejectWithValue("No organization or site ID provided");
    } catch (err) {
      const error = err as AxiosErrorResponse;
      return rejectWithValue(
        error.response?.data?.message || "Failed to activate agent",
      );
    }
  },
);

export const deactivateAgent = createAsyncThunk(
  "insight/deactivateAgent",
  async (
    params: { agentType: AgentType; organizationId?: number; siteId?: number },
    { rejectWithValue },
  ) => {
    try {
      if (params.organizationId) {
        return await InsightService.deactivateOrgAgent(
          params.agentType,
          params.organizationId,
        );
      } else if (params.siteId) {
        return await InsightService.deactivateSiteAgent(
          params.agentType,
          params.siteId,
        );
      }
      return rejectWithValue("No organization or site ID provided");
    } catch (err) {
      const error = err as AxiosErrorResponse;
      return rejectWithValue(
        error.response?.data?.message || "Failed to deactivate agent",
      );
    }
  },
);

export const dismissAlert = createAsyncThunk(
  "insight/dismissAlert",
  async (
    { alertId, reason }: { alertId: number; reason?: string },
    { rejectWithValue },
  ) => {
    try {
      return await InsightService.dismissAlert(alertId, reason);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const acknowledgeAlert = createAsyncThunk(
  "insight/acknowledgeAlert",
  async ({ alertId }: { alertId: number }, { rejectWithValue }) => {
    try {
      return await InsightService.acknowledgeAlert(alertId);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const ignoreAlert = createAsyncThunk(
  "insight/ignoreAlert",
  async (
    { alertId, reason }: { alertId: number; reason?: string },
    { rejectWithValue },
  ) => {
    try {
      return await InsightService.ignoreAlert(alertId, reason);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const remindMeLater = createAsyncThunk(
  "insight/remindMeLater",
  async (
    {
      alertId,
      reminderInMinutes,
    }: { alertId: number; reminderInMinutes?: number },
    { rejectWithValue },
  ) => {
    try {
      return await InsightService.remindMeLater(alertId, reminderInMinutes);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const runAgentForSite = createAsyncThunk(
  "insight/runAgentForSite",
  async (
    { agentType, siteId }: { agentType: AgentType | string; siteId: number },
    { rejectWithValue },
  ) => {
    try {
      return await InsightService.runAgentForSite(agentType, siteId);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const runAgentForOrganization = createAsyncThunk(
  "insight/runAgentForOrganization",
  async (
    {
      agentType,
      organizationId,
    }: { agentType: AgentType | string; organizationId: number },
    { rejectWithValue },
  ) => {
    try {
      return await InsightService.runAgentForOrganization(
        agentType,
        organizationId,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

/** @deprecated Use runAgentForSite */
export const runAgent = createAsyncThunk(
  "insight/runAgent",
  async (
    { agentType, siteId }: { agentType: AgentType | string; siteId: number },
    { rejectWithValue },
  ) => {
    try {
      return await InsightService.runAgentForSite(agentType, siteId);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

/** @deprecated Use getAgentStatusForSite */
export const getAgentStatus = createAsyncThunk(
  "insight/getAgentStatus",
  async ({ siteId }: { siteId: number }, { rejectWithValue }) => {
    try {
      return await InsightService.getAgentStatusForSite(siteId);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

const insightSlice = createSlice({
  name: "insight",
  initialState,
  reducers: {
    setSelectedOrganization: (state, action: PayloadAction<number>) => {
      state.selectedOrganizationId = action.payload;
    },
    setSelectedSite: (state, action: PayloadAction<number>) => {
      state.selectedSiteId = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearLastRunResult: (state) => {
      state.lastRunResult = null;
    },
    clearAlerts: (state) => {
      state.alerts = [];
      state.alertPagination = null;
    },
    updateAlertStatusLocally: (
      state,
      action: PayloadAction<{ alertId: number; status: AlertStatus }>,
    ) => {
      const alert = state.alerts.find((a) => a.id === action.payload.alertId);
      if (alert) alert.status = action.payload.status;
    },
    resetInsight: () => defaultInitialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getGlobalDashboard.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getGlobalDashboard.fulfilled, (state, action) => {
        state.isLoading = false;
        state.globalDashboard = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(getGlobalDashboard.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getSiteDashboard.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getSiteDashboard.fulfilled, (state, action) => {
        state.isLoading = false;
        state.siteDashboard = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(getSiteDashboard.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getTotalAssetsTrend.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getTotalAssetsTrend.fulfilled, (state, action) => {
        state.isLoading = false;
        state.totalAssetsTrend = action.payload;
      })
      .addCase(getTotalAssetsTrend.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getGoodHealthTrend.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getGoodHealthTrend.fulfilled, (state, action) => {
        state.isLoading = false;
        state.goodHealthTrend = action.payload;
      })
      .addCase(getGoodHealthTrend.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getNeedingRepairTrend.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getNeedingRepairTrend.fulfilled, (state, action) => {
        state.isLoading = false;
        state.needingRepairTrend = action.payload;
      })
      .addCase(getNeedingRepairTrend.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getLostMissingTrend.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getLostMissingTrend.fulfilled, (state, action) => {
        state.isLoading = false;
        state.lostMissingTrend = action.payload;
      })
      .addCase(getLostMissingTrend.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getComplianceTrend.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getComplianceTrend.fulfilled, (state, action) => {
        state.isLoading = false;
        state.complianceTrend = action.payload;
      })
      .addCase(getComplianceTrend.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getAssetUtilizationTrend.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAssetUtilizationTrend.fulfilled, (state, action) => {
        state.isLoading = false;
        state.assetUtilizationTrend = action.payload;
      })
      .addCase(getAssetUtilizationTrend.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getGlobalTotalAssetsTrend.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getGlobalTotalAssetsTrend.fulfilled, (state, action) => {
        state.isLoading = false;
        state.globalTotalAssetsTrend = action.payload;
      })
      .addCase(getGlobalTotalAssetsTrend.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getGlobalGoodHealthTrend.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getGlobalGoodHealthTrend.fulfilled, (state, action) => {
        state.isLoading = false;
        state.globalGoodHealthTrend = action.payload;
      })
      .addCase(getGlobalGoodHealthTrend.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getGlobalNeedingRepairTrend.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getGlobalNeedingRepairTrend.fulfilled, (state, action) => {
        state.isLoading = false;
        state.globalNeedingRepairTrend = action.payload;
      })
      .addCase(getGlobalNeedingRepairTrend.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getGlobalLostMissingTrend.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getGlobalLostMissingTrend.fulfilled, (state, action) => {
        state.isLoading = false;
        state.globalLostMissingTrend = action.payload;
      })
      .addCase(getGlobalLostMissingTrend.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getGlobalComplianceTrend.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getGlobalComplianceTrend.fulfilled, (state, action) => {
        state.isLoading = false;
        state.globalComplianceTrend = action.payload;
      })
      .addCase(getGlobalComplianceTrend.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getGlobalAssetUtilizationTrend.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getGlobalAssetUtilizationTrend.fulfilled, (state, action) => {
        state.isLoading = false;
        state.globalAssetUtilizationTrend = action.payload;
      })
      .addCase(getGlobalAssetUtilizationTrend.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getAgentAlerts.pending, (state) => {
        state.isLoadingAlerts = true;
        state.error = null;
      })
      .addCase(getAgentAlerts.fulfilled, (state, action) => {
        state.isLoadingAlerts = false;
        state.alerts = action.payload.alerts;
        state.alertPagination = action.payload.pagination;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(getAgentAlerts.rejected, (state, action) => {
        state.isLoadingAlerts = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getAgentStatusForSite.pending, (state) => {
        state.isLoadingStatus = true;
        state.error = null;
      })
      .addCase(getAgentStatusForSite.fulfilled, (state, action) => {
        state.isLoadingStatus = false;
        state.siteAgentStatus = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(getAgentStatusForSite.rejected, (state, action) => {
        state.isLoadingStatus = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getAgentStatusForOrganization.pending, (state) => {
        state.isLoadingStatus = true;
        state.error = null;
      })
      .addCase(getAgentStatusForOrganization.fulfilled, (state, action) => {
        state.isLoadingStatus = false;
        state.orgAgentStatus = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(getAgentStatusForOrganization.rejected, (state, action) => {
        state.isLoadingStatus = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getAgentStatus.pending, (state) => {
        state.isLoadingStatus = true;
        state.error = null;
      })
      .addCase(getAgentStatus.fulfilled, (state, action) => {
        state.isLoadingStatus = false;
        state.siteAgentStatus = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(getAgentStatus.rejected, (state, action) => {
        state.isLoadingStatus = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(updateAlertTask.fulfilled, (state, action) => {
        const index = state.alerts.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) {
          state.alerts[index] = action.payload;
        }
      })
      .addCase(dismissAlert.pending, (state) => {
        state.isDismissing = true;
        state.error = null;
      })
      .addCase(dismissAlert.fulfilled, (state, action) => {
        state.isDismissing = false;
        const idx = state.alerts.findIndex(
          (a) => a.id === action.payload.alert.id,
        );
        if (idx !== -1) state.alerts[idx] = action.payload.alert;
      })
      .addCase(dismissAlert.rejected, (state, action) => {
        state.isDismissing = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(acknowledgeAlert.pending, (state) => {
        state.isAcknowledging = true;
        state.error = null;
      })
      .addCase(acknowledgeAlert.fulfilled, (state, action) => {
        state.isAcknowledging = false;
        const idx = state.alerts.findIndex(
          (a) => a.id === action.payload.alert.id,
        );
        if (idx !== -1) state.alerts[idx] = action.payload.alert;
      })
      .addCase(acknowledgeAlert.rejected, (state, action) => {
        state.isAcknowledging = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(ignoreAlert.pending, (state) => {
        state.isDismissing = true;
        state.error = null;
      })
      .addCase(ignoreAlert.fulfilled, (state, action) => {
        state.isDismissing = false;
        // Remove dismissed alert from the active list
        state.alerts = state.alerts.filter(
          (a) => a.id !== action.payload.alert.id,
        );
      })
      .addCase(ignoreAlert.rejected, (state, action) => {
        state.isDismissing = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(remindMeLater.pending, (state) => {
        state.isDismissing = true;
        state.error = null;
      })
      .addCase(remindMeLater.fulfilled, (state, action) => {
        state.isDismissing = false;
        const idx = state.alerts.findIndex(
          (a) => a.id === action.payload.alert.id,
        );
        if (idx !== -1) state.alerts[idx] = action.payload.alert;
        // Optionally filter out if you want reminders to also disappear immediately
        // state.alerts = state.alerts.filter((a) => a.id !== action.payload.alert.id);
      })
      .addCase(remindMeLater.rejected, (state, action) => {
        state.isDismissing = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(runAgentForSite.pending, (state) => {
        state.isRunningAgent = true;
        state.error = null;
        state.lastRunResult = null;
      })
      .addCase(runAgentForSite.fulfilled, (state, action) => {
        state.isRunningAgent = false;
        state.lastRunResult = {
          agentType: action.payload.agentType,
          alertsGenerated: action.payload.alertsGenerated,
          siteId: action.payload.siteId,
        };
        if (action.payload.alerts.length > 0) {
          state.alerts = [...action.payload.alerts, ...state.alerts];
        }
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(runAgentForSite.rejected, (state, action) => {
        state.isRunningAgent = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(runAgentForOrganization.pending, (state) => {
        state.isRunningAgent = true;
        state.error = null;
        state.lastRunResult = null;
      })
      .addCase(runAgentForOrganization.fulfilled, (state, action) => {
        state.isRunningAgent = false;
        state.lastRunResult = {
          agentType: action.payload.agentType,
          alertsGenerated: action.payload.alertsGenerated,
          organizationId: action.payload.organizationId,
        };
        if (action.payload.alerts.length > 0) {
          state.alerts = [...action.payload.alerts, ...state.alerts];
        }
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(runAgentForOrganization.rejected, (state, action) => {
        state.isRunningAgent = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(runAgent.pending, (state) => {
        state.isRunningAgent = true;
        state.error = null;
        state.lastRunResult = null;
      })
      .addCase(runAgent.fulfilled, (state, action) => {
        state.isRunningAgent = false;
        state.lastRunResult = {
          agentType: action.payload.agentType,
          alertsGenerated: action.payload.alertsGenerated,
          siteId: action.payload.siteId,
        };
        if (action.payload.alerts.length > 0) {
          state.alerts = [...action.payload.alerts, ...state.alerts];
        }
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(runAgent.rejected, (state, action) => {
        state.isRunningAgent = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setSelectedOrganization,
  setSelectedSite,
  clearError,
  clearLastRunResult,
  clearAlerts,
  updateAlertStatusLocally,
  resetInsight,
} = insightSlice.actions;

export default insightSlice.reducer;
